import { useMemo, useState } from "react";
import { CheckSquare, ListPlus, Pencil, Plus } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  CardTitle,
  EmptyState,
  Subtitle,
  VButton,
  cn,
} from "@/components/ui";
import { findUserByRole } from "@/domains/auth";
import { useTasksByCase, useTaskTemplates } from "@/domains/tasks/api/queries";
import {
  useGenerateTasksFromTemplate,
  useUpdateTaskStatus,
} from "@/domains/tasks/api/mutations";
import { TaskCard } from "@/domains/tasks/components/taskCard";
import { TaskFormDialog } from "@/domains/tasks/components/taskFormDialog";
import type { CameraTask, TaskStatus } from "@/domains/tasks/api/types";

type CaseTasksTabProps = {
  caseId: string;
  startDate: string;
};

type DialogState =
  | { kind: "closed" }
  | { kind: "create"; parentId?: string }
  | { kind: "edit"; task: CameraTask };

export function CaseTasksTab({ caseId, startDate }: CaseTasksTabProps): React.JSX.Element {
  const { data: tasks, isPending } = useTasksByCase(caseId);
  const { data: templates } = useTaskTemplates();
  const generate = useGenerateTasksFromTemplate();
  const updateStatus = useUpdateTaskStatus();
  const [showTemplates, setShowTemplates] = useState(false);
  const [dialog, setDialog] = useState<DialogState>({ kind: "closed" });

  const manager = findUserByRole("manager");
  const partner = findUserByRole("partner");

  const tree = useMemo(() => {
    const list = tasks ?? [];
    const parents = list.filter((t) => !t.parentId);
    const childrenByParent = new Map<string, Array<CameraTask>>();
    for (const t of list) {
      if (!t.parentId) continue;
      const arr = childrenByParent.get(t.parentId) ?? [];
      arr.push(t);
      childrenByParent.set(t.parentId, arr);
    }
    return { parents, childrenByParent };
  }, [tasks]);

  const handleGenerate = (templateId: string): void => {
    if (!manager || !partner) return;
    generate.mutate(
      {
        caseId,
        templateId,
        startDate,
        defaultAssignee: {
          userId: manager.id,
          name: `${manager.firstName} ${manager.lastName}`,
        },
        partnerAssignee: {
          userId: partner.id,
          name: `${partner.firstName} ${partner.lastName}`,
        },
      },
      { onSuccess: () => setShowTemplates(false) },
    );
  };

  const toggleStatus = (task: CameraTask): void => {
    const next: TaskStatus = task.status === "done" ? "todo" : "done";
    updateStatus.mutate({ id: task.id, status: next });
  };

  if (isPending) return <Subtitle>Taken laden…</Subtitle>;

  const list = tasks ?? [];

  const renderRow = (task: CameraTask, isChild = false): React.JSX.Element => {
    const children = tree.childrenByParent.get(task.id) ?? [];
    return (
      <div key={task.id} className={cn(isChild && "ml-8")}>
        <div className="flex items-start gap-2">
          <VButton
            variant="ghost"
            size="icon"
            aria-label={task.status === "done" ? "Markeer als te-doen" : "Markeer als klaar"}
            onClick={() => toggleStatus(task)}
            icon={
              <CheckSquare
                className={
                  task.status === "done"
                    ? "size-5 text-success-600"
                    : "size-5 text-muted-foreground"
                }
                aria-hidden
              />
            }
          />
          <TaskCard task={task} className="flex-1" />
          <div className="flex flex-col gap-1">
            <VButton
              variant="ghost"
              size="icon"
              aria-label="Bewerken"
              onClick={() => setDialog({ kind: "edit", task })}
              icon={<Pencil className="size-4 text-muted-foreground" aria-hidden />}
            />
            {!isChild ? (
              <VButton
                variant="ghost"
                size="icon"
                aria-label="Subtaak toevoegen"
                onClick={() => setDialog({ kind: "create", parentId: task.id })}
                icon={<Plus className="size-4 text-muted-foreground" aria-hidden />}
              />
            ) : null}
          </div>
        </div>
        {children.length > 0 ? (
          <div className="mt-2 space-y-2 border-l-2 border-border pl-4">
            {children.map((c) => renderRow(c, true))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Subtitle>
          {list.length === 0
            ? "Geen taken op dit dossier."
            : `${list.length} taken op dit dossier.`}
        </Subtitle>
        <div className="flex gap-2">
          <VButton variant="outline" onClick={() => setShowTemplates((v) => !v)}>
            <ListPlus className="size-4" aria-hidden />
            Uit template
          </VButton>
          <VButton onClick={() => setDialog({ kind: "create" })}>
            <Plus className="size-4" aria-hidden />
            Nieuwe taak
          </VButton>
        </div>
      </div>

      {showTemplates ? (
        <Card padding="lg" className="space-y-3">
          <CardTitle>Beschikbare templates</CardTitle>
          {(templates ?? []).map((tpl) => (
            <div
              key={tpl.id}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-3"
            >
              <div>
                <Body weight="semibold">{tpl.name}</Body>
                <Caption tone="muted">
                  {tpl.description} — {tpl.items.length} subtaken
                </Caption>
              </div>
              <VButton
                variant="primary"
                size="sm"
                onClick={() => handleGenerate(tpl.id)}
                disabled={generate.isPending}
              >
                Genereren
              </VButton>
            </div>
          ))}
        </Card>
      ) : null}

      {list.length === 0 ? (
        <EmptyState
          title="Geen taken"
          description="Voeg manueel een taak toe, of genereer een takenlijst uit een template."
          action={
            <VButton onClick={() => setDialog({ kind: "create" })}>
              <Plus className="size-4" aria-hidden />
              Nieuwe taak
            </VButton>
          }
        />
      ) : (
        <div className="space-y-3">{tree.parents.map((t) => renderRow(t))}</div>
      )}

      {dialog.kind !== "closed" ? (
        <TaskFormDialog
          open
          onOpenChange={(open) => {
            if (!open) setDialog({ kind: "closed" });
          }}
          mode={
            dialog.kind === "create"
              ? { kind: "create", caseId, parentId: dialog.parentId }
              : { kind: "edit", task: dialog.task }
          }
          defaultDueDate={startDate}
        />
      ) : null}
    </div>
  );
}
